/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Net-earnings estimate for an ERE charging contract. This module is the single
 * source of the formula — the comparison card, the provider profile, the
 * head-to-head page, the comparison grid, the price page and the kennisbank
 * rekentabellen all import from here rather than carrying their own copy. The
 * constants (the reference volumes, the fallback price, the market fee, the
 * ERE factor) and the first lines of the chain live one level further out, in
 * src/lib/offerPricing.js, because the server and the bake need them too and
 * cannot load TypeScript; they are re-exported here under the names every
 * caller always imported.
 *
 * BTW convention: Particulier contracts store
 * commission + service costs INCL 21% BTW (non-deductible for consumers),
 * Zakelijk EX BTW (deductible). Both are used as-is — no extra VAT multiplier.
 *
 * The arithmetic is done in whole EREs and whole cents (2026-09-23). Every
 * worked example on the site prints its operands — "832 ERE × € 0,445" — and
 * until then the sum underneath was computed on 831,75 ERE and rounded only
 * for display, so the line read "832 ERE × € 0,45 = € 370,13" on 111 pages and
 * did not multiply out. Now the ERE count is rounded ONCE, the price keeps its
 * own precision, and each printed line is derived from the previous printed
 * cent amount, so what a reader recomputes is what the page says.
 */

import type { OfferPricing } from '../types/api/offerPricing.js';
import {
  ERE_PRICE,
  MARKET_FEE_PERCENT,
  ereCountFor,
  offerCostCents,
  yieldCents,
} from './offerPricing.js';

export {
  DEFAULT_ANNUAL_KWH,
  DEFAULT_ANNUAL_KWH_BUSINESS,
  ERE_PRICE,
  KWH_TO_ERE_FACTOR,
  MARKET_FEE_PERCENT,
  REFERENCE_KWH,
  defaultAnnualKwh,
  ereCountFor,
} from './offerPricing.js';

/** The four printed lines of a worked example, plus what they were built on. */
export interface EarningsBreakdown {
  /** Whole EREs the example is computed on. */
  eres: number;
  /** €/ERE, as given — never rounded here (formatErePrice prints its precision). */
  price: number;
  /** eres × price, rounded half up to the cent. */
  gross: number;
  /** MARKET_FEE_PERCENT of the printed gross, to the cent. */
  market: number;
  /** commission % of (gross − market), to the cent. */
  commission: number;
  /** Yearly service costs, to the cent. */
  serviceCosts: number;
  /** gross − market − commission − serviceCosts, floored at zero. */
  net: number;
}

/**
 * The breakdown of an offer with a pricing model (src/lib/offerPricing.js):
 * the same lines, plus the vastrecht per kWh and the yearly bound that capped
 * or floored commission + vastrecht. `net` subtracts the clamped total, so
 * "commission + kwhFee" is what the provider charges before the clamp and
 * `clampedTo` (when not null) is what it charges after it.
 */
export interface OfferEarningsBreakdown extends EarningsBreakdown {
  /** The vastrecht per charged kWh, to the cent (0 for every other model). */
  kwhFee: number;
  /** The yearly floor or cap that applied, in €; null when neither did. */
  clampedTo: number | null;
}

/**
 * The cent-exact chain from a whole ERE count. Cents are integers throughout;
 * the price is taken in ten-thousandths (a reading is capped at four decimals
 * in normalizeErePriceEntry), so 832 × 0,445 is 832 × 4 450 ten-thousandths =
 * 37 024 cents exactly. Each later line is computed from the previous line's
 * printed cents, so "€ 370,24 − € 5,55 − € 72,94 = € 291,75" subtracts exactly.
 */
export function earningsFromEres(
  eres: number,
  erePrice: number,
  commissionPct: number,
  serviceCosts = 0,
  marketFeePct = MARKET_FEE_PERCENT
): EarningsBreakdown {
  const { grossCents, marketCents } = yieldCents(eres, erePrice, marketFeePct);
  // A flat commission: the volume plays no part beyond the ERE count.
  const { totalCents } = offerCostCents({
    pricing: { model: 'commission', commissionPct },
    kwh: 0,
    grossCents,
    marketCents,
  });
  const serviceCents = Math.round(serviceCosts * 100);
  const netCents = Math.max(0, grossCents - marketCents - totalCents - serviceCents);
  return {
    eres,
    price: erePrice,
    gross: grossCents / 100,
    market: marketCents / 100,
    commission: totalCents / 100,
    serviceCosts: serviceCents / 100,
    net: netCents / 100,
  };
}

/**
 * The same chain for an offer of any pricing model, at a charged volume: the
 * ERE count of the volume, the gross and the market fee as earningsFromEres
 * prints them, then the model's cost (offerCostCents) — for a flat commission
 * field for field what earningsFromEres answers, plus `kwhFee: 0` and
 * `clampedTo: null`.
 */
export function earningsForOffer(
  kwh: number,
  erePrice: number,
  pricing: OfferPricing,
  serviceCosts = 0,
  marketFeePct = MARKET_FEE_PERCENT
): OfferEarningsBreakdown {
  const eres = ereCountFor(kwh);
  const { grossCents, marketCents } = yieldCents(eres, erePrice, marketFeePct);
  const cost = offerCostCents({ pricing, kwh, grossCents, marketCents });
  const serviceCents = Math.round(serviceCosts * 100);
  const netCents = Math.max(0, grossCents - marketCents - cost.totalCents - serviceCents);
  return {
    eres,
    price: erePrice,
    gross: grossCents / 100,
    market: marketCents / 100,
    commission: cost.commissionCents / 100,
    serviceCosts: serviceCents / 100,
    net: netCents / 100,
    kwhFee: cost.kwhFeeCents / 100,
    clampedTo: cost.clampedCents !== 0 ? cost.totalCents / 100 : null,
  };
}

/**
 * The breakdown for a contract at a charged volume — what the provider page's
 * "Wat levert dit op" prints, line by line.
 * @param erePrice €/ERE — pass the series value from useErePrice(); defaults to
 *        the ERE_PRICE fallback for non-hook contexts.
 * @returns null when volume is 0 or commission/service costs are unknown (we
 *          never project earnings from incomplete data).
 */
export function estimateEarningsBreakdown(
  volume: number,
  commissionFee: number | null,
  serviceCosts: number | null,
  erePrice: number = ERE_PRICE
): EarningsBreakdown | null {
  if (!volume) return null;
  if (commissionFee === null) return null;
  if (serviceCosts === null) return null;
  return earningsFromEres(ereCountFor(volume), erePrice, commissionFee, serviceCosts);
}

/**
 * Estimate net annual payout for a contract — the number on the comparison
 * card. The same chain as the breakdown, so the card and the worked example
 * that explains it can never disagree.
 * @returns euros/year, or null when volume is 0 or commission/service costs are
 *          unknown.
 */
export function estimateNetEarnings(
  volume: number,
  commissionFee: number | null,
  serviceCosts: number | null,
  erePrice: number = ERE_PRICE
): number | null {
  return estimateEarningsBreakdown(volume, commissionFee, serviceCosts, erePrice)?.net ?? null;
}

/**
 * The offer-aware sibling of estimateEarningsBreakdown: the breakdown of an
 * offer with a pricing model (normalizeOfferPricing) at a charged volume.
 * @returns null when volume is 0, the model is unknown (null) or the service
 *          costs are unknown — the same rule: no estimate from incomplete data.
 */
export function estimateOfferBreakdown(
  volume: number,
  pricing: OfferPricing | null,
  serviceCosts: number | null,
  erePrice: number = ERE_PRICE
): OfferEarningsBreakdown | null {
  if (!volume) return null;
  if (pricing === null) return null;
  if (serviceCosts === null) return null;
  return earningsForOffer(volume, erePrice, pricing, serviceCosts);
}

/**
 * The offer-aware sibling of estimateNetEarnings.
 * @returns euros/year, or null under estimateOfferBreakdown's rule.
 */
export function estimateOfferNet(
  volume: number,
  pricing: OfferPricing | null,
  serviceCosts: number | null,
  erePrice: number = ERE_PRICE
): number | null {
  return estimateOfferBreakdown(volume, pricing, serviceCosts, erePrice)?.net ?? null;
}
